import type {
  AiExecutionCheckpoint,
  AiExecutionCheckpointKind,
  AiExecutionPlan,
  AiExecutionPlanStep,
  AiOperationEnvelope,
} from '../../../types/aiOperational'
import { deriveExecutionState } from '../execution/executionStateMachine'

const stepId = (suffix: string) => `exec_${suffix}`

const operationIds = (envelope: AiOperationEnvelope) => envelope.operations.map((operation) => operation.id)

const hasReversibleOperation = (envelope: AiOperationEnvelope) =>
  envelope.operations.some((operation) => operation.rollback?.reversible)

const addStep = (
  steps: AiExecutionPlanStep[],
  patch: Omit<AiExecutionPlanStep, 'operationIds'>,
  envelope: AiOperationEnvelope,
) => {
  steps.push({ ...patch, operationIds: operationIds(envelope) })
}

const addCheckpoint = (
  checkpoints: AiExecutionCheckpoint[],
  checkpoint: AiExecutionCheckpoint & { kind: AiExecutionCheckpointKind },
) => {
  checkpoints.push(checkpoint)
}

export const buildExecutionPlan = (envelope: AiOperationEnvelope): AiExecutionPlan => {
  const steps: AiExecutionPlanStep[] = []
  const checkpoints: AiExecutionCheckpoint[] = []
  const primaryOperation = envelope.operations[0]
  const rollbackStrategy = primaryOperation?.rollback?.strategy ?? 'NOT_REVERSIBLE'

  addStep(steps, {
    id: stepId('context_review'),
    kind: 'CONTEXT_REVIEW',
    title: 'Review bounded workspace context',
    description: 'Use only retrieved records, memory insights, Pulse hints, forecasts, and selected targets relevant to this request.',
    status: 'READY',
    requiresUserAction: false,
    authority: 'CLIENT_LOCAL',
  }, envelope)
  addCheckpoint(checkpoints, {
    id: 'checkpoint_context_digest',
    title: 'Context digest attached',
    description: 'The plan must carry a compact context digest instead of a full workspace dump.',
    requiredBeforeStepId: stepId('simulation_review'),
    satisfiedBy: 'CONTEXT_DIGEST',
    kind: 'DEPENDENCY',
    state: envelope.contextDigest ? 'SATISFIED' : 'PENDING',
  })

  addStep(steps, {
    id: stepId('simulation_review'),
    kind: 'SIMULATION_REVIEW',
    title: 'Preview consequences',
    description: 'Show affected records, risk, forecasts, and expected outcome before any persistent action is prepared.',
    status: envelope.simulation ? 'READY' : 'PLANNED',
    requiresUserAction: false,
    authority: 'CLIENT_LOCAL',
  }, envelope)

  if (envelope.risk === 'RESTRICTED') {
    addStep(steps, {
      id: stepId('refusal_boundary'),
      kind: 'REFUSAL_BOUNDARY',
      title: 'Stop at restricted boundary',
      description: 'Do not execute destructive, admin, economy, permission, integration, or automation-activation actions from this surface.',
      status: 'BLOCKED',
      requiresUserAction: false,
      authority: 'TRUSTED_BACKEND',
    }, envelope)
    addCheckpoint(checkpoints, {
      id: 'checkpoint_refusal',
      title: 'Restricted action blocked',
      description: 'The request can only be reframed as safe planning or sent through a dedicated governed surface.',
      requiredBeforeStepId: stepId('refusal_boundary'),
      satisfiedBy: 'REFUSAL',
      kind: 'REFUSAL',
      state: 'BLOCKED',
    })
  } else if (envelope.risk === 'LOW') {
    addStep(steps, {
      id: stepId('guided_ui'),
      kind: 'GUIDED_UI',
      title: 'Start guided local workflow',
      description: 'Proceed with guidance, navigation, summary, preview, or tour behavior without mutating workspace data.',
      status: 'READY',
      requiresUserAction: false,
      authority: 'CLIENT_LOCAL',
    }, envelope)
  } else if (envelope.risk === 'MEDIUM') {
    addStep(steps, {
      id: stepId('user_confirmation'),
      kind: 'USER_CONFIRMATION',
      title: 'Ask for explicit confirmation',
      description: 'Confirm the operational direction before preparing a proposal or guided execution path.',
      status: 'PLANNED',
      requiresUserAction: true,
      authority: 'USER_CONFIRMATION',
    }, envelope)
    addCheckpoint(checkpoints, {
      id: 'checkpoint_confirmation',
      title: 'User confirmation required',
      description: 'Medium-risk work cannot advance until the user confirms the direction.',
      requiredBeforeStepId: stepId('user_confirmation'),
      satisfiedBy: 'USER_CONFIRMATION',
      kind: 'USER_CONFIRMATION',
      state: 'WAITING',
      waitingReason: 'Awaiting explicit confirmation before proposal preparation.',
    })
  } else {
    addStep(steps, {
      id: stepId('proposal_review'),
      kind: 'PROPOSAL_REVIEW',
      title: 'Create reviewable proposal',
      description: 'Prepare a proposal with affected records, risk, simulation, and rollback metadata for human review.',
      status: 'WAITING',
      requiresUserAction: true,
      authority: 'AI_PROPOSAL_REVIEW',
    }, envelope)
    addCheckpoint(checkpoints, {
      id: 'checkpoint_proposal_approval',
      title: 'Proposal approval required',
      description: 'High-risk persistent changes require approval before trusted execution.',
      requiredBeforeStepId: stepId('trusted_execution'),
      satisfiedBy: 'PROPOSAL_APPROVAL',
      kind: 'APPROVAL',
      state: 'WAITING',
      waitingReason: 'Awaiting proposal approval.',
    })
    addStep(steps, {
      id: stepId('trusted_execution'),
      kind: 'TRUSTED_EXECUTION',
      title: 'Execute through trusted mutation path',
      description: 'Approved changes must run through proposal executors, TimelineContext, trusted callables, or DeltaProcessor.',
      status: 'WAITING',
      requiresUserAction: false,
      authority: primaryOperation?.kind === 'CHECKLIST_STATUS' || primaryOperation?.kind === 'CHECKLIST_DEPENDENCY'
        ? 'DELTA_PROCESSOR'
        : 'TRUSTED_BACKEND',
    }, envelope)
  }

  addStep(steps, {
    id: stepId('audit_capture'),
    kind: 'AUDIT_CAPTURE',
    title: 'Capture audit trail',
    description: 'Important operations must preserve actor, intent, source surface, and rollback references.',
    status: envelope.risk === 'LOW' ? 'PLANNED' : 'READY',
    requiresUserAction: false,
    authority: 'AUDIT_LEDGER',
  }, envelope)

  if (hasReversibleOperation(envelope)) {
    addStep(steps, {
      id: stepId('rollback_ready'),
      kind: 'ROLLBACK_READY',
      title: 'Keep rollback path available',
      description: 'Attach rollback strategy metadata so approved operations can be reversed through existing audit/undo semantics where supported.',
      status: 'PLANNED',
      requiresUserAction: false,
      authority: 'AUDIT_LEDGER',
    }, envelope)
    addCheckpoint(checkpoints, {
      id: 'checkpoint_rollback',
      title: 'Rollback metadata attached',
      description: 'Reversible operations must carry audit replay, inverse operation, or snapshot restoration metadata.',
      requiredBeforeStepId: stepId('rollback_ready'),
      satisfiedBy: 'AUDIT_ROLLBACK',
      kind: 'AUDIT',
      state: 'PENDING',
    })
  }

  return {
    version: 1,
    title: primaryOperation?.title ?? 'Operational execution plan',
    summary: envelope.risk === 'LOW'
      ? 'Guidance-only workflow that can start locally.'
      : envelope.risk === 'RESTRICTED'
        ? 'Restricted workflow blocked before execution.'
        : 'Review-first workflow that prepares safe trusted execution.',
    risk: envelope.risk,
    state: deriveExecutionState(envelope),
    canAutoStart: envelope.risk === 'LOW' && envelope.operations.every((operation) => operation.allowedAutoExecute),
    steps,
    checkpoints,
    rollbackStrategy,
    generatedAtIso: new Date().toISOString(),
  }
}
